import store from '@/store/indexStore';
import { AZURE_CDN } from '@/composable/constant';

/**
 * @vuese
 * 拡張子取得処理<br>
 * ファイル名から拡張子を取得する
 * @arg fileName ファイル名
 */
export function getExtension(fileName) {
  return fileName.split('.').pop().trim().toLowerCase();
}

/**
 * @vuese
 * 画像ファイル判定処理<br>
 * ファイル名から拡張子を取得して判定処理をする
 * @arg fileName ファイル名
 */
export function isImageFile(fileName) {
  return fileName
    ? ['jpeg', 'jpg', 'png', 'heic', 'webp', 'gif'].includes(getExtension(fileName))
    : true;
}

/**
 * @vuese
 * 秒数表示加工処理<br>
 * 秒数を計算してmm:ss形式で返却する
 * @arg sec 秒数
 */
export function convertSecTommss(sec) {
  const roundedSec = Math.round(sec);
  const videoDurationMin = Math.floor(roundedSec / 60);
  const videoDurationSec = roundedSec % 60;
  const videoDurationMinStr =
    videoDurationMin.toString().length === 1 ? `0${videoDurationMin}` : videoDurationMin;
  const videoDurationSecStr =
    videoDurationSec.toString().length === 1 ? `0${videoDurationSec}` : videoDurationSec;
  return `${videoDurationMinStr}:${videoDurationSecStr}`;
}

/**
 * @vuese
 * 秒数表示加工処理<br>
 * 秒数を計算してm分s秒形式で返却する
 * @arg sec 秒数
 */
export function convertSecToms(sec) {
  const roundedSec = Math.round(sec);
  const videoDurationMin = Math.floor(roundedSec / 60);
  const videoDurationSec = roundedSec % 60;
  const videoDurationMinStr = videoDurationMin;
  const videoDurationSecStr = videoDurationSec;
  if (videoDurationMin === 0) {
    return `${videoDurationSecStr}秒`;
  }
  return `${videoDurationMinStr}分${videoDurationSecStr}秒`;
}

/**
 * @vuese
 * base64エンコード処理<br>
 * 読み込んだファイルをpromiseで返却する
 * @arg file base64エンコード対象ファイル
 */
export function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

/**
 * @vuese
 * ファイルサイズの計算処理<br>
 * ファイルサイズをMBで表示できるよう計算して返却する
 * @arg size ファイルサイズ
 */
export function calcSize(size) {
  const bytes = Number(size);
  const mega = 1048576;
  const result = bytes / mega;

  return Math.round(result * 100) / 100;
}

export function processFile(file, src, resolve, materialFileDataSet) {
  getBase64(file)
    .then((image) => {
      // eslint-disable-next-line no-param-reassign
      materialFileDataSet = {
        materialFile: file,
        icon: image,
        src: `${src}`,
        imageFlag: isImageFile(file.name),
        fileSize: calcSize(file.size),
      };
      resolve(materialFileDataSet);
    })
    .catch(() => {
      resolve(materialFileDataSet);
    });
}

/**
 * @vuese
 * ファイル選択時本処理<br>
 * 選択したファイルについてリサイズ、base64エンコードを行いファイルおよび<br>
 * 画像選択時表示制御用フラグをセットして返却する
 * @arg event 選択ファイル
 */
export function onFileChange(e) {
  const files = e.target.files || e.dataTransfer.files;
  if (e.target.files.length > 1) {
    return Promise.all(
      Array.from(files).map((file) => {
        return new Promise((resolve) => {
          const materialFileDataSet = {
            materialFile: '',
            icon: '',
            uploadedImageFlag: false,
            fileSize: 0,
          };
          let src = '';
          if (!isImageFile(file.name)) {
            src = (window.URL || window.webkitURL).createObjectURL(file);
            const xhr = new XMLHttpRequest();
            xhr.open('GET', src, true);
            xhr.responseType = 'blob';
            xhr.onload = () => {
              if (xhr.status === 200) {
                src = (window.URL || window.webkitURL || window || {}).createObjectURL(
                  xhr.response
                );
                processFile(file, src, resolve, materialFileDataSet);
              }
            };
            xhr.send();
          } else {
            processFile(file, src, resolve, materialFileDataSet);
          }
        });
      })
    );
  }
  return new Promise((resolve) => {
    // 返却値の初期化
    let materialFileDataSet = {
      materialFile: '',
      icon: '',
      uploadedImageFlag: false,
      fileSize: 0,
    };
    let src = '';
    if (!isImageFile(files[0].name)) {
      src = (window.URL || window.webkitURL).createObjectURL(files[0]);
      const xhr = new XMLHttpRequest();
      xhr.open('GET', src, true);
      xhr.responseType = 'blob';
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4 && xhr.status === 200) {
          src = (window.URL || window.webkitURL || window || {}).createObjectURL(xhr.response);
        }
      };
      xhr.send();
    }
    if (files.length > 0) {
      getBase64(files[0])
        .then((image) => {
          materialFileDataSet = {
            materialFile: files[0],
            icon: image,
            src: `${src}`,
            imageFlag: isImageFile(files[0].name),
            fileSize: calcSize(files[0].size),
          };
          resolve(materialFileDataSet);
        })
        .catch();
    } else {
      resolve(materialFileDataSet);
    }
  });
}

/**
 * @vuese
 * 拡張子変換処理<br>
 * ファイルの拡張子をjpegに変換する
 * @arg fileName ファイル名
 */
export function convertExtension(fileName) {
  return `${fileName.split('.')[0]}.jpeg`;
}

/**
 * @vuese
 * 素材サムネイル処理<br>
 * サムネイル画像を返却する
 * @arg fileName ファイル名
 */
export function getThumbnailUrl(fileName) {
  if (isImageFile(fileName)) {
    return `${fileName.split('.')[0]}_thumbnail.jpeg`;
  }
  return convertExtension(fileName);
}

/**
 * @vuese
 * 画像GIF判定処理<br>
 * 素材番号のファイル名からGIFファイルか判定し、GIFファイルならばタイムスタンプを付与して画像URLを返却する
 * @arg materialNum 素材番号
 */
export function getImageUrl(materialNum, contentMaterialList, timestamp) {
  if (contentMaterialList.length === 0) return '';
  return (
    AZURE_CDN.ENDPOINT +
    contentMaterialList[materialNum].materialUrl +
    AZURE_CDN.SAS +
    (getExtension(contentMaterialList[materialNum].materialUrl) === 'gif'
      ? `&timestamp=${timestamp}`
      : '')
  );
}

/**
 * @vuese
 * 引数のフレーム名からBlobStorageのURLを生成して返す
 *
 * @arg frameName フレーム名
 */
export function getContentFrameUrl(frameName) {
  return AZURE_CDN.ENDPOINT + frameName + AZURE_CDN.SAS;
}

/**
 * @vuese
 * 素材再配置処理<br>
 * 素材番号の素材について、Vuexにない場合は中央切り抜きをセットしたオフセットとスケール、ある場合はその値を返却する
 * @arg materialNum 素材番号
 * @arg event 描画領域
 */
export function reallocateMaterial(
  materialNum,
  contentMaterialList,
  isFullScreenPreviewMode,
  event
) {
  const storedContentMaterialList = store.getters['content/getContentMaterialList'];
  const storedContentMaterial = storedContentMaterialList[materialNum];
  const displayAreaWidth = event.target.parentNode.getBoundingClientRect().width;
  const displayAreaHeight = event.target.parentNode.getBoundingClientRect().height;
  let materialWidth;
  let materialHeight;
  if (isFullScreenPreviewMode) {
    if (isImageFile(contentMaterialList[materialNum].materialUrl)) {
      materialWidth = event.target.naturalWidth;
      materialHeight = event.target.naturalHeight;
    } else {
      materialWidth = event.target.videoWidth;
      materialHeight = event.target.videoHeight;
    }
  } else {
    materialWidth = event.target.naturalWidth;
    materialHeight = event.target.naturalHeight;
  }
  let offsetX = 0;
  let offsetY = 0;
  let scale = 1.0;
  if (storedContentMaterial.scale === 0) {
    // scaleが未設定(0)の場合
    const displayAreaAspectRatio = displayAreaHeight / displayAreaWidth;
    const imageAspectRatio = materialHeight / materialWidth;
    if (displayAreaAspectRatio > imageAspectRatio) {
      offsetX =
        (-1 * (displayAreaHeight * materialWidth - materialHeight * displayAreaWidth)) /
        (2 * materialHeight * displayAreaWidth);

      scale = (displayAreaHeight * materialWidth) / (materialHeight * displayAreaWidth);
    } else {
      offsetY =
        (-1 * (materialHeight * displayAreaWidth - displayAreaHeight * materialWidth)) /
        (2 * displayAreaHeight * materialWidth);
    }
    storedContentMaterial.offsetX = offsetX;
    storedContentMaterial.offsetY = offsetY;
    storedContentMaterial.scale = scale;
    storedContentMaterialList.splice(materialNum, 1, storedContentMaterial);
    store.commit('content/setContentMaterialList', storedContentMaterialList);
  } else {
    offsetX = storedContentMaterial.offsetX;
    offsetY = storedContentMaterial.offsetY;
    scale = storedContentMaterial.scale;
  }
  return {
    displayAreaWidth,
    displayAreaHeight,
    materialWidth,
    materialHeight,
    offsetX,
    offsetY,
    scale,
  };
}

/**
 * 素材配置処理<br>
 * 表示領域を取得し、呼び出し元が利用する形で返す
 * @param {*} contentMaterial コンテンツ素材
 * @param {*} event 描画領域
 */
export function allocateMaterial(contentMaterial, isFullScreenPreviewMode, event) {
  const displayAreaWidth = event.target.parentNode.getBoundingClientRect().width;
  const displayAreaHeight = event.target.parentNode.getBoundingClientRect().height;
  let materialWidth;
  let materialHeight;
  if (isFullScreenPreviewMode) {
    if (isImageFile(contentMaterial.materialUrl)) {
      materialWidth = event.target.naturalWidth;
      materialHeight = event.target.naturalHeight;
    } else {
      materialWidth = event.target.videoWidth;
      materialHeight = event.target.videoHeight;
    }
  } else {
    materialWidth = event.target.naturalWidth;
    materialHeight = event.target.naturalHeight;
  }
  const { offsetX, offsetY, scale } = contentMaterial;

  return {
    displayAreaWidth,
    displayAreaHeight,
    materialWidth,
    materialHeight,
    offsetX,
    offsetY,
    scale,
  };
}

// ポーリングを停止する関数
// ポーリング開始は各コンポーネントで実装する
export function stopPolling(pollingInterval) {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    // eslint-disable-next-line no-param-reassign
    pollingInterval = null;
  }
}

/**
 * @vuese
 * 音声ボタン表示変更処理<br>
 * プレビュ領域に表示されている音声ボタンを押下するたびにisAudioEnabledの値を変更させる
 * @arg materialNum 素材番号
 */
export function changeIsAudioEnabled(materialNum, isEditing) {
  // 編集中でない場合はボタンを押下されても何もしない
  if (!isEditing) {
    return;
  }

  // isAudioEnabledの情報をストアから取得
  const contentMaterialList = store.getters['content/getContentMaterialList'];

  // isAudioEnabledの値を反転させる
  contentMaterialList[materialNum].isAudioEnabled =
    contentMaterialList[materialNum].isAudioEnabled === 1 ? 0 : 1;

  // 反転させたisAudioEnabledの情報をストアに保存
  store.commit('content/setContentMaterialList', contentMaterialList);
}
